import type { 
  GetDomainsManagementPlatformServiceDomainsCountData,
  GetDomainsManagementPlatformServiceDomainsCountResponse,

  GetDomainsManagementPlatformServiceDomainsData,
  GetDomainsManagementPlatformServiceDomainsResponse,

  PostDomainsManagementPlatformServiceDomainsData,
  PostDomainsManagementPlatformServiceDomainsResponse,

  GetDomainsManagementPlatformServiceDomainsByIdData,
  GetDomainsManagementPlatformServiceDomainsByIdResponse,

  PutDomainsManagementPlatformServiceDomainsByIdData,
  PutDomainsManagementPlatformServiceDomainsByIdResponse,

  DeleteDomainsManagementPlatformServiceDomainsByIdData,
  DeleteDomainsManagementPlatformServiceDomainsByIdResponse,

  PostDomainsManagementPlatformServiceDomainsByIdRecheckData,
  PostDomainsManagementPlatformServiceDomainsByIdRecheckResponse,

  PutDomainsManagementPlatformServiceDomainsByIdBlockedCountriesData,
  PutDomainsManagementPlatformServiceDomainsByIdBlockedCountriesResponse,

  DeleteDomainsManagementPlatformServiceDomainsByIdBlockedCountriesData,
  DeleteDomainsManagementPlatformServiceDomainsByIdBlockedCountriesResponse
} from "@/types/openapi-ts";
import type { AxiosInstance } from "axios";


export function getServiceDomainsApiModule(affiliatesApi: AxiosInstance) {
  return {
    /**
     * Get service domains count
     *
     * @param {Object} params
     * @param {String} params.search
     * @param {String} params.platform_service_origin_id
     * @param {String} params.platform_server_id
     * @param {String} params.domain_registrar_account_id
     * @param {String} params.dns_hosting_account_id
     * @param {String} params.ssl_issuance
     * @param {String} params.ns_management
     * @param {String} params.dns_management
     *
     * @return {Promise<Object>} api response
     */
    getServiceDomainsCount(params: GetDomainsManagementPlatformServiceDomainsCountData['query']) {
      return affiliatesApi.get<GetDomainsManagementPlatformServiceDomainsCountResponse>(
        '/domains-management/platform-service-domains/count',
        { params }
      );
    },

    /**
     * Get service domains
     *
     * @param {Object} params
     * @param {String} params.sort_column
     * @param {String} params.sort_dir
     * @param {String} params.search
     * @param {Number} params.limit
     * @param {Number} params.offset
     * @param {String} params.platform_service_origin_id
     * @param {String} params.platform_server_id
     * @param {String} params.domain_registrar_account_id
     * @param {String} params.dns_hosting_account_id
     * @param {String} params.ssl_issuance
     * @param {String} params.ns_management
     * @param {String} params.dns_management
     *
     * @return {Promise<Object>} api response
     */
    getServiceDomains(params: GetDomainsManagementPlatformServiceDomainsData['query']) {
      return affiliatesApi.get<GetDomainsManagementPlatformServiceDomainsResponse>(
        '/domains-management/platform-service-domains',
        { params }
      );
    },

    /**
     * Create service domain
     *
     * @param {Object} data
     * @param {String} data.platform_service_origin_id
     * @param {String} data.domain_name
     * @param {String} data.pick_platform_server_type
     * @param {String} data.platform_server_id
     * @param {String} data.platform_server_ipv4_address_id
     * @param {String} data.platform_server_ipv6_address_id
     * @param {String} data.ssl_issuance
     * @param {String} data.ns_management
     * @param {String} data.domain_registrar_account_id
     * @param {Array} data.ns_servers
     * @param {String} data.dns_management
     * @param {String} data.dns_hosting_account_id
     *
     * @return {Promise<Object>} api response
     */
    createServiceDomain(data: PostDomainsManagementPlatformServiceDomainsData['body']) {
      return affiliatesApi.post<PostDomainsManagementPlatformServiceDomainsResponse>(
        '/domains-management/platform-service-domains',
        data
      );
    },

    /**
     * Get service domain by id
     *
     * @param {String} id
     *
     * @return {Promise<Object>} api response
     */
    getServiceDomainById(id: string) {
      return affiliatesApi.get<GetDomainsManagementPlatformServiceDomainsByIdResponse>(
        `/domains-management/platform-service-domains/${id}`
      );
    },

    /**
     * Update service domain
     *
     * @param {String} id
     * @param {Object} data
     * @param {String} data.platform_server_id
     * @param {String} data.platform_server_ipv4_address_id
     * @param {String} data.platform_server_ipv6_address_id
     * @param {String} data.ssl_issuance
     * @param {String} data.ns_management
     * @param {String} data.domain_registrar_account_id
     * @param {Array} data.ns_servers
     * @param {String} data.dns_management
     * @param {String} data.dns_hosting_account_id
     *
     * @return {Promise<Object>} api response
     */
    updateServiceDomain(id: string, data: PutDomainsManagementPlatformServiceDomainsByIdData['body']) {
      return affiliatesApi.put<PutDomainsManagementPlatformServiceDomainsByIdResponse>(
        `/domains-management/platform-service-domains/${id}`,
        data
      );
    },

    /**
     * Delete service domain by id
     *
     * @param {String} id
     *
     * @return {Promise<Object>} api response
     */
    deleteServiceDomain(id: string) {
      return affiliatesApi.delete<DeleteDomainsManagementPlatformServiceDomainsByIdResponse>(
        `/domains-management/platform-service-domains/${id}`
      );
    },

    /**
     * Re-Check service domain by id
     *
     * @param {String} id
     *
     * @return {Promise<Object>} api response
     */
    reCheckServiceDomain(id: string) {
      return affiliatesApi.post<PostDomainsManagementPlatformServiceDomainsByIdRecheckResponse>(
        `/domains-management/platform-service-domains/${id}/recheck`
      );
    },

    updateBlockedCountry(
      id: string,
      { country_code }: PutDomainsManagementPlatformServiceDomainsByIdBlockedCountriesData['body']
    ) {
      return affiliatesApi.put<PutDomainsManagementPlatformServiceDomainsByIdBlockedCountriesResponse>(
        `/domains-management/platform-service-domains/${id}/blocked-countries`,
        { country_code }
      );
    },
    deleteBlockedCountry(
      id: string,
      { country_code }: DeleteDomainsManagementPlatformServiceDomainsByIdBlockedCountriesData['body']
    ) {
      return affiliatesApi.delete<DeleteDomainsManagementPlatformServiceDomainsByIdBlockedCountriesResponse>(
        `/domains-management/platform-service-domains/${id}/blocked-countries`,
        { data: { country_code } }
      );
    },
  }
}